import React, { useEffect, useRef } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { SidebarTW } from "./SidebarTW";
import { FooterTW } from "./FooterTW";
import { SidebarProvider } from "../../contexts/SidebarContext";
import { ChatBubble } from "../support/ChatBubble";
import { ChatPanel } from "../support/ChatPanel";
import { ChatProvider } from "../../contexts/ChatContext";

/**
 * AuthenticatedLayout - Persistenter Layout-Wrapper für eingeloggte User
 *
 * Die Sidebar wird nur EINMAL gerendert und bleibt beim Seitenwechsel
 * bestehen. Nur der Content-Bereich (<Outlet />) wird ausgetauscht.
 *
 * Struktur:
 * ┌─────────────┬──────────────────────────────────────┐
 * │             │                                      │
 * │   Sidebar   │          <Outlet />                  │
 * │   (einmal)  │          (wechselt)                  │
 * │             │                                      │
 * │             │                                      │
 * └─────────────┴──────────────────────────────────────┘
 *
 * Der SidebarProvider signalisiert den Kind-Komponenten (z.B. SidebarLayout),
 * dass die Sidebar bereits existiert und nicht erneut gerendert werden muss.
 */

// Inner Layout Komponente (braucht Context)
const AuthenticatedLayoutContent: React.FC = () => {
  const location = useLocation();
  const contentRef = useRef<HTMLElement>(null);

  // Auf der Chart-/Asset-Detail-Seite der Investment-Academy wird die
  // Sidebar komplett ausgeblendet, damit der Chart die volle Breite nutzt
  // (besonders auf kleineren Bildschirmen). Der Content bekommt dann auch
  // kein linkes Sidebar-Padding.
  const isChartFullscreen = /^\/academy\/plan-trade\/asset\//.test(
    location.pathname
  );

  // Bei Seitenwechsel: Scroll-Position zurücksetzen
  useEffect(() => {
    // Scroll den Content-Container nach oben
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
    // Fallback: Auch window scrollen falls nötig
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <>
      {/* Full-height layout container */}
      <div className="min-h-screen bg-gray-100 dark:bg-gray-950">
        {/* Sidebar - Tailwind-only, mit integriertem Mobile-Trigger.
            Auf der Chart-Vollbild-Seite komplett ausgeblendet. */}
        {!isChartFullscreen && <SidebarTW />}

        {/* Main Content - rechts neben der fixen Sidebar ab Desktop (xl: >=1280px).
            Auf Mobile/Tablet (inkl. Querformat) kein Padding, da Sidebar Off-Canvas ist.
            Im Chart-Vollbild kein Sidebar-Padding, damit der Chart die volle Breite nutzt. */}
        <main
          ref={contentRef}
          className={`min-h-screen flex flex-col bg-gray-100 dark:bg-gray-950 ${
            isChartFullscreen ? '' : 'xl:pl-[220px]'
          }`}
        >
          {/* Page Content - min-h-screen sorgt dafür, dass der Content allein
              schon mindestens Viewport-Höhe hat. Der Footer kommt dann garantiert
              erst NACH dem ersten Scrollen ins Bild, nicht direkt darunter. */}
          <div className="min-h-screen bg-gray-100 dark:bg-gray-950 relative z-10">
            <Outlet />
          </div>

          {/* Footer ohne Eigenfarbe — nur Glas-Effekt durch backdrop-blur.
              relative + z-10, damit er ÜBER dem fixed Page-Background (z.B.
              Bitcoin-Logo z-0) liegt und backdrop-filter es verschwimmen kann. */}
          <div className="relative z-10 border-t border-gray-200 dark:border-white/5 backdrop-blur-lg">
            <FooterTW className="bg-transparent" />
          </div>
        </main>

        {/* Premium-Support-Chat — Schicht 1 (Layout-Gate):
            Existiert NUR im AuthenticatedLayout, niemals im PublicLayout.
            Schicht 2 (Render-Gate) entscheidet via useChatAccess, ob Bubble
            und Panel für den aktuellen User überhaupt gerendert werden. */}
        <ChatBubble />
        <ChatPanel />
      </div>
    </>
  );
};

// Haupt-Export mit Provider
export const AuthenticatedLayout: React.FC = () => {
  return (
    <SidebarProvider hasSidebar={true}>
      <ChatProvider>
        <AuthenticatedLayoutContent />
      </ChatProvider>
    </SidebarProvider>
  );
};

export default AuthenticatedLayout;
